import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { hasPickedLanguage, getQueryLang, saveLang, applyRTL, detectDeviceLang } from "@/lib/i18n";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppShell from "@/components/AppShell";

import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import LanguagePicker from "@/pages/LanguagePicker";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import FirmJoin from "@/pages/FirmJoin";
import Showcase from "@/pages/Showcase";
import SharedPlanView from "@/pages/SharedPlanView";

import Studio from "@/pages/Studio";
import Projects from "@/pages/Projects";
import Wizard from "@/pages/Wizard";
import DesignWizard from "@/pages/DesignWizard";
import Variants from "@/pages/Variants";
import Templates from "@/pages/Templates";
import Editor from "@/pages/Editor";
import FloorPlans from "@/pages/deliverables/FloorPlans";
import Elevations from "@/pages/deliverables/Elevations";
import Section from "@/pages/deliverables/Section";
import Foundation from "@/pages/deliverables/Foundation";
import Materials from "@/pages/deliverables/Materials";
import Schedules from "@/pages/deliverables/Schedules";
import Takeoff from "@/pages/deliverables/Takeoff";
import MepReport from "@/pages/deliverables/MepReport";
import CodeCheck from "@/pages/deliverables/CodeCheck";
import Account from "@/pages/Account";
import Upgrade from "@/pages/Upgrade";
import Firm from "@/pages/Firm";
import Contact from "@/pages/Contact";
import Methodology from "@/pages/Methodology";
import CodeCompare from "@/pages/CodeCompare";

import AdminConsole from "@/pages/admin/AdminConsole";

// Keep <link rel="canonical"> in sync with the current route so search engines
// index each page as a distinct URL instead of treating them all as duplicates
// of "/" (the static default baked into index.html).
function CanonicalUpdater() {
  const loc = useLocation();
  useEffect(() => {
    try {
      const el = document.getElementById("abl-canonical");
      if (el) el.setAttribute("href", window.location.origin + loc.pathname);
    } catch { /* canonical is best-effort */ }
  }, [loc.pathname]);
  return null;
}


function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Authenticated users go to the app. Anonymous visitors (incl. Googlebot)
  // get the Landing content RENDERED at "/" - no client redirect - so the
  // homepage is directly indexable (fixes GSC "Page with redirect").
  if (user) return <Navigate to="/studio" replace />;
  return <Landing />;
}

// Public, crawler-indexable routes. The language gate must NEVER redirect
// these to /language, otherwise Googlebot sees a redirect instead of content
// and the page is dropped from the index.
const PUBLIC_PATHS = new Set([
  "/", "/landing", "/showcase", "/methodology", "/contact",
  "/legal/privacy", "/legal/terms", "/login", "/register",
  "/language", "/firm/join",
]);

// First-launch gate: force language selection before entering the APP only.
function LanguageGate({ children }) {
  const loc = useLocation();
  if (!hasPickedLanguage()) {
    // Honor SEO deep-links like /landing?lang=es - pick that language and
    // skip the gate so search visitors land straight in their language.
    const ql = getQueryLang();
    if (ql) {
      saveLang(ql);
      applyRTL(ql);
    } else if (PUBLIC_PATHS.has(loc.pathname) || loc.pathname.startsWith("/view/")) {
      // Indexable public page OR a public shared-plan link (/view/:token):
      // render content with a sensible default language (no forced redirect,
      // no saved choice) so crawlers/clients see the content directly.
      applyRTL(detectDeviceLang());
    } else if (loc.pathname !== "/language") {
      const next = loc.pathname && loc.pathname !== "/" ? loc.pathname : "/";
      return <Navigate to={`/language?next=${encodeURIComponent(next)}`} replace />;
    }
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors />
        <CanonicalUpdater />
        <LanguageGate>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/language" element={<LanguagePicker />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/firm/join" element={<FirmJoin />} />
          <Route path="/showcase" element={<Showcase />} />
          <Route path="/view/:token" element={<SharedPlanView />} />
          {/* Public marketing/info pages - indexable by search engines
           *  (previously behind auth, which made Google see a login
           *  redirect). They only need the UI language, not a session. */}
          <Route path="/methodology" element={<Methodology />} />
          <Route path="/contact" element={<Contact />} />

          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/studio" element={<Studio />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/wizard" element={<Wizard />} />
            <Route path="/design-wizard" element={<DesignWizard />} />
            <Route path="/variants" element={<Variants />} />
            <Route path="/templates" element={<Templates />} />
            <Route path="/editor" element={<Editor />} />
            <Route path="/floor-plans" element={<FloorPlans />} />
            <Route path="/elevations" element={<Elevations />} />
            <Route path="/section" element={<Section />} />
            <Route path="/foundation" element={<Foundation />} />
            <Route path="/materials" element={<Materials />} />
            <Route path="/schedules" element={<Schedules />} />
            <Route path="/takeoff" element={<Takeoff />} />
            <Route path="/mep-report" element={<MepReport />} />
            <Route path="/code-check" element={<CodeCheck />} />
            <Route path="/code-compare" element={<CodeCompare />} />
            <Route path="/account" element={<Account />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/firm" element={<Firm />} />
          </Route>

          <Route
            element={
              <ProtectedRoute adminOnly>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route path="/admin" element={<AdminConsole />} />
            <Route path="/admin/:tab" element={<AdminConsole />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </LanguageGate>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
